import toast from 'react-hot-toast';
import {
  ArchiveIcon,
  ArrowCounterClockwiseIcon,
  DotsThreeIcon,
  EyeIcon,
  EyeSlashIcon,
  TrashIcon,
} from '@phosphor-icons/react';

import { archiveAgentSession, deleteAgentSession, setPrWatch, type AgentSession } from '@/api-models/agent-sessions';
import { IconButton } from '@/ui/button/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/ui/DropdownMenu';

/**
 * Why the watcher stopped, for the menu item's tooltip.
 *
 * Worth carrying: "watching is off" invites a shrug, whereas "someone pushed to
 * this branch" tells you why and what it means to turn it back on.
 */
const WATCH_STOPPED_REASON: Record<string, string> = {
  external_commit: 'Someone pushed to this branch from outside the session.',
  wake_limit: 'The session was woken too many times without the problem clearing.',
  budget: 'The session reached its budget limit.',
  pr_merged: 'The pull request was merged.',
  pr_closed: 'The pull request was closed.',
  token_invalid: 'Your GitHub connection is no longer valid.',
  provider_error: 'GitHub could not be reached.',
  user: 'You turned watching off.',
};

interface SessionActionsMenuProps {
  session: AgentSession;
  /** Refresh the caller's list/detail cache once the action lands. */
  onChanged: () => void;
  /** Called after a successful delete, e.g. to navigate away from a detail page. */
  onDeleted?: () => void;
}

/**
 * Watch / archive / restore / delete for one session. Used by the list rows and
 * the session header.
 */
export function SessionActionsMenu({ session, onChanged, onDeleted }: SessionActionsMenuProps) {
  const archived = Boolean(session.archived_at);

  // Only sessions that can have a pull request can watch one. A plan session has
  // no branch and no sandbox, so the item would be inert.
  const canWatch = Boolean(session.branch) && session.mode !== 'plan';
  const watching = session.pr_watch_enabled;

  async function handleWatch() {
    try {
      await setPrWatch(session.id, !watching);
      onChanged();
      toast.success(watching ? 'Stopped watching this pull request' : 'Watching this pull request');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not change watching');
    }
  }

  async function handleArchive() {
    try {
      await archiveAgentSession(session.id, !archived);
      onChanged();
      toast.success(archived ? 'Session restored' : 'Session archived');
    } catch {
      toast.error(archived ? 'Could not restore session' : 'Could not archive session');
    }
  }

  async function handleDelete() {
    if (!window.confirm('Delete this session? Its chat history and sandbox are removed for good.')) return;
    try {
      await deleteAgentSession(session.id);
      onChanged();
      onDeleted?.();
      toast.success('Session deleted');
    } catch {
      toast.error('Could not delete session');
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <IconButton size={8} aria-label="Session actions" onClick={(e) => e.stopPropagation()}>
          <DotsThreeIcon size={18} weight="bold" />
        </IconButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        {canWatch && (
          <>
            <DropdownMenuItem
              onSelect={() => void handleWatch()}
              title={
                watching
                  ? 'The agent is woken for merge conflicts, CI failures and reviews on this pull request.'
                  : `Not watching this pull request. ${
                      (session.pr_watch_stopped_reason && WATCH_STOPPED_REASON[session.pr_watch_stopped_reason]) ?? ''
                    }`.trim()
              }
            >
              {watching ? <EyeSlashIcon size={15} /> : <EyeIcon size={15} />}
              {watching ? 'Stop watching PR' : 'Watch PR'}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuItem onSelect={() => void handleArchive()}>
          {archived ? <ArrowCounterClockwiseIcon size={15} /> : <ArchiveIcon size={15} />}
          {archived ? 'Restore' : 'Archive'}
        </DropdownMenuItem>
        <DropdownMenuItem destructive onSelect={() => void handleDelete()}>
          <TrashIcon size={15} />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
